<template>
  <div class="new-tag-form">
    <div class="new-tag-form__inner">
      <!-- Main title-->
      <div class="new-tag-form__main-title">
        {{ mode === 'season' ? 'Add New Season' : 'Add New Tag' }}
      </div>

      <fs-input
        v-model="name"
        class="new-tag-form__new-tag-name-input"
        :placeholder="mode === 'season' ? 'New season name' : 'New tag name'"
      />

      <!-- Channel date ranges (for seasons only) -->
      <div v-if="mode === 'season'" class="channel-activities">
        <div v-for="channel in useChannels().loaded" :key="channel.id" class="channel-activities__channel">
          <div class="channel-activities__channel-label">{{ channel.name }}</div>
          <div class="channel-activities__picker-row">
            <fs-date-picker
              v-model="channelActivities[channel.id]"
              class="channel-activities__date-picker"
              range
              @update:model-value="() => checkOverlaps(channel.id)"
            />
            <warning-triangle
              v-if="overlaps[channel.id] && overlaps[channel.id].length > 0"
              class="channel-activities__warning"
              :text="getOverlapWarning(channel.id)"
            />
          </div>
        </div>
      </div>

      <!-- Category -->
      <div
        v-else
        :class="{
          'category-dropdown': true,
          'new-tag-form__category-dropdown': true,
        }"
      >
        <div class="category-dropdown__title">Category</div>

        <fs-select
          v-model="selectedTagCategory"
          class="category-dropdown__dropdown"
          placeholder="Tag group"
          :options="tagStore.loaded"
          label="name"
          :is-disabled="!!lockedTagCategory"
        >
          <template #selected="{ selected }">{{ categoryLabel(selected) }}</template>
          <template #default="{ option }">{{ categoryLabel(option) }}</template>
        </fs-select>
      </div>

      <color-picker-table
        class="new-tag-form__colors-table"
        :colors="[...useColors().baseColors].slice(0, 24)"
        :border-radius="19"
        check-mode
        show-color-picker
        tabindex="0"
        @color-pick="setNewTagColor"
      />

      <button-pair
        class="add-new-component__manage-buttons"
        :is-loading="isSaveInProgress"
        action-btn-name="Add"
        :action-btn-icon="addCircleIcon"
        :is-disabled="!isAllRequiredFieldsFilled"
        @cancel="$emit('cancel')"
        @action="addNewTag"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ButtonPair from '@components/button-pair.vue';
import ColorPickerTable from '@components/color-pickers/table.vue';
import FsDatePicker from '@components/form-elements/fs-date-picker.vue';
import FsSelect from '@components/form-elements/selects/fs-select/index.vue';
import FsInput from '@components/form-elements/fs-input.vue';
import WarningTriangle from '@components/warning-triangle.vue';
import addCircleIcon from '@icons/other/add-circle.svg';
import { computed, reactive, ref, watch } from 'vue';
import { useColors } from '@/store/colors';
import { type TagType, useTagStore } from '@lib/store-helper';
import { useChannels } from '@/store/channels';
import { useSeasons } from '@/store/seasons';
import { useApi } from '@lib/api';
import { useUser } from '@/store/user';
import { dateToDateOnlyString } from '@lib/formatters';
import type { OverlapWarning, TagCategoryDto } from '@/ApiClient';

const emits = defineEmits<{
  cancel: [];
}>();

const props = withDefaults(
  defineProps<{
    startingName?: string;
    mode: 'season' | TagType;
    tagGroupId?: number;
    addTagFunc: (
      name: string,
      color: string,
      categoryId: number,
      channelActivities?: Record<number, [Date, Date] | undefined>
    ) => Promise<void>;
  }>(),
  {
    startingName: '',
    mode: 'product',
  }
);

const tagType = computed(() => (props.mode === 'season' ? 'product' : props.mode));
const tagStore = computed(() => useTagStore(tagType.value));
const lockedTagCategory = props.tagGroupId ? tagStore.value.loaded.find((c) => c.id === props.tagGroupId) : undefined;
const selectedTagCategory = ref(lockedTagCategory ?? tagStore.value.baseCategory);

// Product-level categories get a trailing "(P)" so they're distinguishable from article-level ones in
// the dropdown, where their names can otherwise look identical.
function categoryLabel(category?: TagCategoryDto | null): string {
  const name = category?.name ?? '';
  return category?.productLevel ? `${name} (P)` : name;
}

const name = ref('');
const newTagColor = ref<string>('');
const isSaveInProgress = ref(false);

// Channel activities (for seasons)
const api = useApi();
const seasons = useSeasons();
const channelActivities = reactive<Record<number, [Date, Date] | undefined>>({});
const overlaps = ref<Record<number, OverlapWarning[]>>({});

async function checkOverlaps(channelId: number) {
  const dateRange = channelActivities[channelId];
  if (!dateRange || !dateRange[0] || !dateRange[1]) {
    overlaps.value[channelId] = [];
    return;
  }

  try {
    const result = await api.season_CheckOverlapsForNew(useUser().brandId, {
      channelId,
      start: dateToDateOnlyString(dateRange[0]),
      end: dateToDateOnlyString(dateRange[1]),
    });
    overlaps.value[channelId] = result.warnings;
  } catch (error) {
    console.error('Failed to check overlaps:', error);
    overlaps.value[channelId] = [];
  }
}

function getOverlapWarning(channelId: number): string {
  const warnings = overlaps.value[channelId];
  if (!warnings || warnings.length === 0) return '';

  const formatDate = (date: string | null | undefined): string => {
    if (!date) return '';
    return date.split('T')[0]; // Extract just the date part (YYYY-MM-DD)
  };

  return warnings
    .map((w) => {
      const seasonName = seasons.byId[w.seasonId]?.name ?? 'Unknown Season';
      const from =
        w.currentStart && w.currentEnd
          ? `${formatDate(w.currentStart)} ~ ${formatDate(w.currentEnd)}`
          : 'undefined range';

      // Check if activity will be completely removed
      if (!w.newStart && !w.newEnd) {
        return `This will remove ${seasonName}`;
      }

      const to = w.newStart && w.newEnd ? `${formatDate(w.newStart)} ~ ${formatDate(w.newEnd)}` : 'removed';
      return `This will shrink ${seasonName} from ${from} to ${to}`;
    })
    .join('\n');
}

watch(
  () => props.startingName,
  (newVal) => (name.value = newVal),
  { immediate: true }
);

const isAllRequiredFieldsFilled = computed(() => {
  return name.value && newTagColor.value && (props.mode === 'season' || selectedTagCategory.value);
});

async function addNewTag() {
  if (!isAllRequiredFieldsFilled.value) {
    return;
  }

  isSaveInProgress.value = true;
  await props.addTagFunc(name.value, newTagColor.value, selectedTagCategory.value?.id, channelActivities);
  isSaveInProgress.value = false;

  emits('cancel');
}

function setNewTagColor(color: { r: number; g: number; b: number } | undefined) {
  if (!color) {
    newTagColor.value = '';
    return;
  }

  const { r, g, b } = color;
  newTagColor.value = `rgb(${r},${g},${b})`;
}
</script>

<style scoped lang="scss">
@import '@styles/variables';

// Category Dropdown & Dates Range --------------------------------------------
.category-dropdown,
.dates-range {
  box-sizing: border-box;

  &__title {
    margin-bottom: 0.5rem;
    padding-left: 2rem;
    font-size: 1.2rem;
    color: rgba($c-dark, 0.5);
    text-align: left;
  }

  &__dropdown {
    width: 19rem;
    margin: 0;
  }

  &__date-picker {
    width: 19rem;
    margin: 0;
  }
}

// New tag form ---------------------------------------------------------------
.new-tag-form {
  padding: 3.5rem 4rem 3rem;

  &__inner {
    width: 39rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-content: center;
    position: relative;
  }

  &__main-title {
    margin-bottom: 2rem;
    font:
      2rem/2.6rem 'Montserrat',
      sans-serif;
    color: rgba($c-dark, 1);
    text-align: center;
  }

  &__new-tag-name-input {
    width: calc(100% - 1.7rem * 2);
    margin-bottom: 1.2rem;
  }

  &__dates-range {
    width: calc(100% - 1.7rem * 2);
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 2;
  }

  &__category-dropdown {
    width: calc(100% - 1.7rem * 2);
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 2;
  }

  &__colors-table {
    width: 100%;
    box-sizing: border-box;
    padding: 1rem;
    margin-bottom: 3.5rem;
    position: relative;
    z-index: 1;
    overflow: hidden;
    outline: none;

    &::before {
      content: '';
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 3;
      box-shadow:
        inset 0 0 0.9rem rgba(255, 255, 255, 1),
        inset 0 0 0.9rem rgba(255, 255, 255, 1);
      pointer-events: none;
    }
  }
}

// Channel activities ---------------------------------------------------------
.channel-activities {
  width: calc(100% - 1.7rem * 2);
  margin-bottom: 1.5rem;

  &__channel {
    margin-bottom: 1.2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__channel-label {
    margin-bottom: 0.5rem;
    padding-left: 2rem;
    font-size: 1.2rem;
    color: rgba($c-dark, 0.5);
    text-align: left;
  }

  &__picker-row {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__date-picker {
    flex: 1;
    margin: 0;
  }

  &__warning {
    display: flex;
    align-items: center;
  }
}
</style>
